import { Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import {
  copyStorePaymentOptions,
  getStorePaymentOptions,
  saveStorePaymentOptions,
  getCountryPaymentOptions,
} from '../../../../utils/service-calls/payment-setup';
import { ButtonBlack } from '../../../reusable';

const MODES = { CONFIGURE: 'CONFIGURE', COPY: 'COPY' };
const MODE_LABELS = { [MODES.CONFIGURE]: 'Configure Store', [MODES.COPY]: 'Copy Store' };
const PAYMENT_OPTION_TYPES = ['POSITIVE', 'NEGATIVE', 'NEGATIVE_NO_TENDER'];
const ADD_NEW_VALUE = '__ADD_NEW__';

const emptyRow = () => ({
  isCustomMediaType: false,
  isCustomPaymentType: false,
  mediaType: '',
  paymentOptionType: 'POSITIVE',
  paymentType: '',
  rowId: uuidv4(),
});

const PaymentConfigTab = ({ selectedCountry, hasNextTab, onSaveAndNext }) => {
  const userToken = useSelector((state) => state.authorizationReducer.auth.accessToken);

  const [mode, setMode] = useState(MODES.COPY);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // --- Country reference options (shared) ---
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryOptionsLoaded, setCountryOptionsLoaded] = useState(false);
  const [countryLoading, setCountryLoading] = useState(false);
  const [showCountryOverlay, setShowCountryOverlay] = useState(false);

  // --- Copy Store state ---
  const [sourceStoreId, setSourceStoreId] = useState('');
  const [destStoreId, setDestStoreId] = useState('');
  const [override, setOverride] = useState(false);
  const [excludeTypes, setExcludeTypes] = useState([]);
  const [sourceOptions, setSourceOptions] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(false);

  // --- Configure Store state ---
  const [configStoreId, setConfigStoreId] = useState('');
  const [existingOptions, setExistingOptions] = useState(null);
  const [configRows, setConfigRows] = useState(() => [emptyRow()]);
  const [configLoading, setConfigLoading] = useState(false);

  // Fetch country options on mount / country change for dropdown population
  const fetchCountryOptions = useCallback(async () => {
    if (!selectedCountry?.value) return;
    setCountryLoading(true);
    try {
      const res = await getCountryPaymentOptions(userToken, selectedCountry.value);
      setCountryOptions(res.body?.paymentOptions || []);
      setCountryOptionsLoaded(true);
    } catch {
      setCountryOptions([]);
      setCountryOptionsLoaded(true);
    } finally {
      setCountryLoading(false);
    }
  }, [userToken, selectedCountry]);

  useEffect(() => {
    setCountryOptionsLoaded(false);
    setCountryOptions([]);
    if (selectedCountry?.value) {
      fetchCountryOptions();
    }
  }, [selectedCountry?.value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive unique payment types and media types from country options
  const countryPaymentTypes = useMemo(
    () => [...new Set(countryOptions.map((o) => o.paymentType).filter(Boolean))],
    [countryOptions],
  );
  const countryMediaTypes = useMemo(
    () => [...new Set(countryOptions.map((o) => o.mediaType).filter(Boolean))],
    [countryOptions],
  );

  // ---- Copy Store handlers ----

  const handleFetchSourceOptions = useCallback(async () => {
    if (!sourceStoreId.trim()) return;
    setSourceLoading(true);
    setStatus(null);
    try {
      const res = await getStorePaymentOptions(userToken, sourceStoreId.trim());
      setSourceOptions(res.body?.paymentOptions || []);
    } catch (err) {
      setStatus({ isError: true, message: `Failed to fetch source store: ${err?.message || 'Unknown error'}` });
      setSourceOptions(null);
    } finally {
      setSourceLoading(false);
    }
  }, [userToken, sourceStoreId]);

  const handleOverrideToggle = useCallback(() => {
    if (!override) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        'Enabling Override will permanently delete ALL existing payment options at the destination store before copying. Are you sure?',
      );
      if (!confirmed) return;
    }
    setOverride((prev) => !prev);
  }, [override]);

  const handleCopy = useCallback(async () => {
    if (!sourceStoreId.trim() || !destStoreId.trim()) return;
    setLoading(true);
    setStatus(null);
    try {
      const body = {
        destStoreId: destStoreId.trim(),
        excludePaymentTypes: excludeTypes.length > 0 ? excludeTypes : undefined,
        override,
        sourceStoreId: sourceStoreId.trim(),
      };
      const res = await copyStorePaymentOptions(userToken, body);
      const { copiedCount = 0, deletedCount = 0 } = res.body || {};
      setStatus({
        isError: false,
        message: `Copy succeeded — ${copiedCount} option(s) copied${override ? `, ${deletedCount} deleted` : ''}.`,
      });
    } catch (err) {
      setStatus({ isError: true, message: `Copy failed: ${err?.message || 'Unknown error'}` });
    } finally {
      setLoading(false);
    }
  }, [userToken, sourceStoreId, destStoreId, override, excludeTypes]);

  // ---- Configure Store handlers ----

  const handleFetchConfigStore = useCallback(async () => {
    if (!configStoreId.trim()) return;
    setConfigLoading(true);
    setStatus(null);
    try {
      const res = await getStorePaymentOptions(userToken, configStoreId.trim());
      const options = res.body?.paymentOptions || [];
      setExistingOptions(options);
      if (options.length > 0) {
        setConfigRows(options.map((o) => ({
          isCustomMediaType: !countryMediaTypes.includes(o.mediaType),
          isCustomPaymentType: !countryPaymentTypes.includes(o.paymentType),
          mediaType: o.mediaType || '',
          paymentOptionType: o.paymentOptionType || 'POSITIVE',
          paymentType: o.paymentType || '',
          rowId: uuidv4(),
        })));
      } else {
        setConfigRows([emptyRow()]);
      }
    } catch (err) {
      setStatus({ isError: true, message: `Failed to fetch store: ${err?.message || 'Unknown error'}` });
      setExistingOptions(null);
    } finally {
      setConfigLoading(false);
    }
  }, [userToken, configStoreId, countryPaymentTypes, countryMediaTypes]);

  const handleAddRow = useCallback(() => {
    setConfigRows((prev) => [...prev, emptyRow()]);
  }, []);

  const handleRemoveRow = useCallback((index) => {
    setConfigRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [emptyRow()];
    });
  }, []);

  const handleRowChange = useCallback((index, field, value) => {
    setConfigRows((prev) => prev.map((row, i) => {
      if (i !== index) return row;
      if (field === 'paymentType') {
        if (value === ADD_NEW_VALUE) {
          return { ...row, isCustomPaymentType: true, paymentType: '' };
        }
        return { ...row, isCustomPaymentType: false, paymentType: value };
      }
      if (field === 'mediaType') {
        if (value === ADD_NEW_VALUE) {
          return { ...row, isCustomMediaType: true, mediaType: '' };
        }
        return { ...row, isCustomMediaType: false, mediaType: value };
      }
      if (field === 'customPaymentType') {
        return { ...row, paymentType: value };
      }
      if (field === 'customMediaType') {
        return { ...row, mediaType: value };
      }
      return { ...row, [field]: value };
    }));
  }, []);

  const handleSaveConfig = useCallback(async () => {
    const validRows = configRows
      .filter((r) => r.paymentType.trim() && r.mediaType.trim())
      .map(({ mediaType, paymentOptionType, paymentType }) => ({
        mediaType, paymentOptionType, paymentType,
      }));
    if (!configStoreId.trim() || validRows.length === 0) return;
    setLoading(true);
    setStatus(null);
    try {
      const body = { paymentOptions: validRows };
      const res = await saveStorePaymentOptions(userToken, configStoreId.trim(), body);
      const { savedCount = 0, skippedCount = 0 } = res.body || {};
      setStatus({
        isError: false,
        message: `Save succeeded — ${savedCount} option(s) saved, ${skippedCount} skipped (duplicates).`,
      });
      if (hasNextTab && onSaveAndNext) {
        onSaveAndNext();
      }
    } catch (err) {
      setStatus({ isError: true, message: `Save failed: ${err?.message || 'Unknown error'}` });
    } finally {
      setLoading(false);
    }
  }, [userToken, configStoreId, configRows, hasNextTab, onSaveAndNext]);

  // ---- Render helpers ----

  const renderSegmentedControl = () => {
    const modes = [MODES.COPY, MODES.CONFIGURE];
    const activeIndex = modes.indexOf(mode);
    return (
      <div className="soa-workflow-section">
        <div className="soa-segmented">
          <div
            className="soa-segmented-indicator"
            style={{
              transform: `translateX(${activeIndex * 100}%)`,
              width: `calc((100% - 8px) / ${modes.length})`,
            }}
          />
          {modes.map((m) => (
            <button
              key={m}
              className={`soa-segmented-btn ${mode === m ? 'soa-segmented-btn-active' : ''}`}
              type="button"
              onClick={() => { setMode(m); setStatus(null); }}
            >
              <span className="soa-segmented-label">{MODE_LABELS[m]}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderPaymentOptionsTable = (options, caption) => {
    if (!options || options.length === 0) {
      return <p className="soa-payment-empty">No payment options found{caption ? ` ${caption}` : ''}.</p>;
    }
    return (
      <table className="soa-table soa-payment-table">
        <thead>
          <tr>
            <th>Payment Option Type</th>
            <th>Payment Type</th>
            <th>Media Type</th>
          </tr>
        </thead>
        <tbody>
          {options.map((opt, i) => (
            <tr key={`${opt.paymentOptionType}-${opt.paymentType}-${opt.mediaType}`} className={i % 2 === 0 ? '' : 'soa-row-even'}>
              <td>
                <span className={`pill ${opt.paymentOptionType === 'POSITIVE' ? 'pill-positive' : 'pill-negative'}`}>
                  {opt.paymentOptionType}
                </span>
              </td>
              <td>{opt.paymentType}</td>
              <td>{opt.mediaType}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderCountryOverlay = () => {
    if (!showCountryOverlay) return null;
    return (
      <div
        aria-label="Close country payment options reference"
        className="soa-payment-overlay-backdrop"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowCountryOverlay(false);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
            setShowCountryOverlay(false);
          }
        }}
      >
        <div
          aria-modal="true"
          className="soa-payment-overlay"
          role="dialog"
          tabIndex={-1}
        >
          <div className="soa-payment-overlay-header">
            <h3 className="soa-payment-overlay-title">
              {selectedCountry?.value || 'Country'} — Payment Options Reference
            </h3>
            <button
              aria-label="Close country payment options reference"
              className="soa-payment-overlay-close"
              type="button"
              onClick={() => setShowCountryOverlay(false)}
            >
              &times;
            </button>
          </div>
          <div className="soa-payment-overlay-body">
            {countryLoading && <Loading />}
            {!countryLoading && renderPaymentOptionsTable(countryOptions, `for ${selectedCountry?.value || 'this country'}`)}
          </div>
        </div>
      </div>
    );
  };

  const renderCopyStore = () => {
    const bothIdsEntered = sourceStoreId.trim() && destStoreId.trim();
    return (
      <div className="soa-payment-form">
        <div className="soa-payment-field">
          <label className="soa-payment-label" htmlFor="source-store-id">
            Source Store ID <span className="soa-payment-req">*</span>
          </label>
          <div className="soa-payment-input-row">
            <input
              className="soa-payment-input"
              id="source-store-id"
              placeholder="e.g. 3226440f-a201-4a6e-a4f4-4022f5e99fa6"
              type="text"
              value={sourceStoreId}
              onChange={(e) => setSourceStoreId(e.target.value)}
            />
            <button
              className="soa-payment-fetch-btn"
              disabled={!sourceStoreId.trim() || sourceLoading}
              type="button"
              onClick={handleFetchSourceOptions}
            >
              {sourceLoading ? 'Fetching...' : 'Fetch Options'}
            </button>
          </div>
          <span className="soa-payment-hint">Store to copy payment options FROM.</span>
        </div>

        {sourceOptions !== null && (
          <div className="soa-payment-preview">
            <div className="soa-payment-preview-title">
              Source Store Payment Options ({sourceOptions.length})
            </div>
            {renderPaymentOptionsTable(sourceOptions, 'for this store')}
          </div>
        )}

        <div className="soa-payment-field">
          <label className="soa-payment-label" htmlFor="dest-store-id">
            Destination Store ID <span className="soa-payment-req">*</span>
          </label>
          <input
            className="soa-payment-input"
            id="dest-store-id"
            placeholder="e.g. 71e4d1dc-0ee6-4ab9-8367-dee909de6403"
            type="text"
            value={destStoreId}
            onChange={(e) => setDestStoreId(e.target.value)}
          />
          <span className="soa-payment-hint">Store to copy payment options TO.</span>
        </div>

        {bothIdsEntered && (
          <>
            <div className="soa-payment-field">
              <span className="soa-payment-label">Override Existing Options</span>
              <div className="soa-payment-toggle-row">
                <button
                  aria-checked={override}
                  aria-label="Override existing options"
                  className={`soa-payment-switch ${override ? 'soa-payment-switch-on' : ''}`}
                  role="switch"
                  type="button"
                  onClick={handleOverrideToggle}
                >
                  <span className="soa-payment-switch-knob" />
                </button>
                <span className="soa-payment-toggle-label">
                  Override: {override ? 'ON' : 'OFF'}
                </span>
              </div>
              {override && (
                <div className="soa-payment-override-warning">
                  All existing payment options at the destination store will be deleted before copying.
                </div>
              )}
            </div>

            <div className="soa-payment-field">
              <span className="soa-payment-label">Exclude Payment Types</span>
              {sourceOptions !== null && sourceOptions.length > 0 ? (
                <div className="soa-payment-checkbox-list">
                  {[...new Set(sourceOptions.map((opt) => opt.paymentType))].map((type) => (
                    <label key={type} className="soa-payment-checkbox-item">
                      <input
                        checked={excludeTypes.includes(type)}
                        type="checkbox"
                        onChange={() => {
                          setExcludeTypes((prev) => (prev.includes(type)
                            ? prev.filter((t) => t !== type)
                            : [...prev, type]));
                        }}
                      />
                      <span className="soa-payment-checkbox-label">{type}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <span className="soa-payment-hint">Fetch source store options first to see available payment types.</span>
              )}
              <span className="soa-payment-hint">Payment types to skip during the copy.</span>
            </div>

            <div className="soa-payment-actions">
              <ButtonBlack
                isDisabled={!sourceStoreId.trim() || !destStoreId.trim() || loading}
                isLoading={loading}
                label="Copy Payment Options"
                onClick={handleCopy}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  const renderConfigureStore = () => {
    const validRows = configRows.filter((r) => r.paymentType.trim() && r.mediaType.trim());
    const canSave = configStoreId.trim() && validRows.length > 0 && !loading;
    return (
      <div className="soa-payment-form">
        <div className="soa-payment-field">
          <label className="soa-payment-label" htmlFor="config-store-id">
            Store ID <span className="soa-payment-req">*</span>
          </label>
          <div className="soa-payment-input-row">
            <input
              className="soa-payment-input"
              id="config-store-id"
              placeholder="e.g. 71e4d1dc-0ee6-4ab9-8367-dee909de6403"
              type="text"
              value={configStoreId}
              onChange={(e) => setConfigStoreId(e.target.value)}
            />
            <button
              className="soa-payment-fetch-btn"
              disabled={!configStoreId.trim() || configLoading}
              type="button"
              onClick={handleFetchConfigStore}
            >
              {configLoading ? 'Fetching...' : 'Fetch Options'}
            </button>
          </div>
          <span className="soa-payment-hint">The store being configured.</span>
        </div>

        {existingOptions !== null && existingOptions.length > 0 && (
          <div className="soa-payment-preview">
            <div className="soa-payment-preview-title">
              Existing Payment Options ({existingOptions.length})
            </div>
            {renderPaymentOptionsTable(existingOptions, 'currently saved')}
          </div>
        )}
        {existingOptions !== null && existingOptions.length === 0 && (
          <div className="soa-payment-empty-state">
            No payment options configured for this store yet.
          </div>
        )}

        <div className="soa-payment-field">
          <div className="soa-payment-config-header">
            <span className="soa-payment-label">Payment Options</span>
            <button
              className="soa-payment-fetch-btn soa-payment-country-btn"
              disabled={!selectedCountry?.value || countryLoading || !countryOptionsLoaded}
              type="button"
              onClick={() => setShowCountryOverlay(true)}
            >
              {`View ${selectedCountry?.value || 'Country'} Payment Options`}
            </button>
          </div>

          {!countryOptionsLoaded && countryLoading && (
            <p className="soa-payment-hint">Loading country payment options for dropdowns...</p>
          )}

          <table className="soa-table soa-payment-table soa-payment-edit-table">
            <thead>
              <tr>
                <th>Payment Option Type</th>
                <th>Payment Type</th>
                <th>Media Type</th>
                <th aria-label="Actions" style={{ width: '80px' }} />
              </tr>
            </thead>
            <tbody>
              {configRows.map((row, i) => (
                <tr key={row.rowId} className={i % 2 === 0 ? '' : 'soa-row-even'}>
                  <td>
                    <select
                      aria-label={`Payment option type for row ${i + 1}`}
                      className="soa-payment-select"
                      value={row.paymentOptionType}
                      onChange={(e) => handleRowChange(i, 'paymentOptionType', e.target.value)}
                    >
                      {PAYMENT_OPTION_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </td>

                  {/* Payment Type — dropdown from country options + Add New */}
                  <td>
                    {row.isCustomPaymentType ? (
                      <div className="soa-payment-custom-input-row">
                        <input
                          aria-label={`Custom payment type for row ${i + 1}`}
                          className="soa-payment-cell-input"
                          placeholder="Enter new payment type"
                          type="text"
                          value={row.paymentType}
                          onChange={(e) => handleRowChange(i, 'customPaymentType', e.target.value)}
                        />
                        <button
                          aria-label={`Back to payment type dropdown for row ${i + 1}`}
                          className="soa-payment-custom-cancel"
                          title="Back to dropdown"
                          type="button"
                          onClick={() => handleRowChange(i, 'paymentType', '')}
                        >
                          &times;
                        </button>
                      </div>
                    ) : (
                      <select
                        aria-label={`Payment type for row ${i + 1}`}
                        className="soa-payment-select"
                        value={row.paymentType}
                        onChange={(e) => handleRowChange(i, 'paymentType', e.target.value)}
                      >
                        <option value="">— Select —</option>
                        {countryPaymentTypes.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                        <option value={ADD_NEW_VALUE}>+ Add New Type</option>
                      </select>
                    )}
                  </td>

                  {/* Media Type — dropdown from country options + Add New */}
                  <td>
                    {row.isCustomMediaType ? (
                      <div className="soa-payment-custom-input-row">
                        <input
                          aria-label={`Custom media type for row ${i + 1}`}
                          className="soa-payment-cell-input"
                          placeholder="Enter new media type"
                          type="text"
                          value={row.mediaType}
                          onChange={(e) => handleRowChange(i, 'customMediaType', e.target.value)}
                        />
                        <button
                          aria-label={`Back to media type dropdown for row ${i + 1}`}
                          className="soa-payment-custom-cancel"
                          title="Back to dropdown"
                          type="button"
                          onClick={() => handleRowChange(i, 'mediaType', '')}
                        >
                          &times;
                        </button>
                      </div>
                    ) : (
                      <select
                        aria-label={`Media type for row ${i + 1}`}
                        className="soa-payment-select"
                        value={row.mediaType}
                        onChange={(e) => handleRowChange(i, 'mediaType', e.target.value)}
                      >
                        <option value="">— Select —</option>
                        {countryMediaTypes.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                        <option value={ADD_NEW_VALUE}>+ Add New Type</option>
                      </select>
                    )}
                  </td>

                  <td>
                    <button
                      className="soa-payment-remove-btn"
                      type="button"
                      onClick={() => handleRemoveRow(i)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="soa-payment-add-row" type="button" onClick={handleAddRow}>
            + Add another payment option
          </button>
        </div>

        <div className="soa-payment-actions">
          <ButtonBlack
            isDisabled={!canSave}
            isLoading={loading}
            label={hasNextTab ? 'Save and Continue' : 'Save Payment Options'}
            onClick={handleSaveConfig}
          />
        </div>
      </div>
    );
  };

  return (
    <section className="soa-payment-tab">
      {renderSegmentedControl()}

      {status && (
        <p className={`soa-status-message ${status.isError ? 'soa-status-warning' : 'soa-status-success'}`}>
          {status.message}
        </p>
      )}

      {mode === MODES.COPY && renderCopyStore()}
      {mode === MODES.CONFIGURE && renderConfigureStore()}

      {renderCountryOverlay()}
    </section>
  );
};

PaymentConfigTab.defaultProps = {
  hasNextTab: false,
  onSaveAndNext: null,
  selectedCountry: null,
};

PaymentConfigTab.propTypes = {
  hasNextTab: PropTypes.bool,
  onSaveAndNext: PropTypes.func,
  selectedCountry: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
};

export default PaymentConfigTab;
